import { Group } from '@mantine/core';
import type { EmbeddedPromptOut } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { EntityType } from '@repo/common/enums/entityType';
import { Button, Icon, IconButton, Text } from '@repo/foundations';
import { memo, useCallback } from 'react';
import {
	useAuthUser,
	useQuestionsEnabled,
	type IEmbeddedPrompt,
} from '../../../api';
import { useTemplateList } from '../../../api/hooks/templates';
import { createEntityModalStore } from '../../../features/CreateEntityModal';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { entityModalStore } from '../../EntityModal/store';
import { sanitizePromptMessage } from '../utils';

export const AskQuestionButton = memo(
	({
		message,
		parentPrompt,
		source,
		variant = 'default',
	}: {
		message: IEmbeddedPrompt | EmbeddedPromptOut;
		parentPrompt?: IEmbeddedPrompt | EmbeddedPromptOut;
		source: 'prompt' | 'response';
		variant?: 'default' | 'tertiary';
	}) => {
		const { user: authUser, workspace } = useAuthUser();
		const questionsEnabled = useQuestionsEnabled();
		const { aiQuestionWorkflow } = useFeatureFlags();

		const { data: defaultQuestionTemplates } = useTemplateList({
			filters: {
				is_default: true,
				entity_type_template: EntityType.question,
			},
		});

		const showQuestionModal = useCallback(() => {
			const { prompt } = message;

			if (!prompt) {
				return;
			}

			trackEvent(
				'question/create_from_ai_clicked',
				{ source },
				authUser,
				workspace
			);

			const sanitizedPrompt = sanitizePromptMessage(prompt)
				.replace(/(<([^>]+)>)/gi, '')
				.replace(/\n+/g, ' ');
			const markdownLinkRegex = /!?\[.*?\]\(.*?\)/g;
			const title = sanitizedPrompt.replace(markdownLinkRegex, '').trim();
			const attachments = sanitizedPrompt.match(markdownLinkRegex) || [];

			if (aiQuestionWorkflow) {
				const lastResponse =
					message.structured_response?.[message.structured_response.length - 1];

				const allLines = lastResponse?.content.split('\n');
				const blockquotedLines = allLines?.map((line) => `> ${line}`);
				const blockquotedPrompt = blockquotedLines?.join('\n');

				createEntityModalStore.open({
					entityType: EntityType.question,
					template: defaultQuestionTemplates?.results?.[0],
					title: title,
					description: `Secoda AI said:\n${blockquotedPrompt}`,
					metadata: {
						owners: [message.user?.id].filter(Boolean),
						ai_prompt: parentPrompt,
					},
					attachment: {
						type: 'ai_chat_message',
						attachmentId: message.id,
					},
				});
			} else {
				entityModalStore.open({
					type: EntityType.question,
					template: defaultQuestionTemplates?.results?.[0],
					title: title || '',
					description: attachments.join('\n') || '',
					metadata: {
						owners: [message.user?.id].filter(Boolean),
						ai_prompt: parentPrompt ?? message,
					},
					withLinkToAI: true,
				});
			}
		}, [
			message,
			source,
			authUser,
			workspace,
			aiQuestionWorkflow,
			defaultQuestionTemplates?.results,
			parentPrompt,
		]);

		if (!questionsEnabled) {
			return null;
		}

		if (variant === 'tertiary') {
			return (
				<IconButton
					iconName="messageCircleQuestion"
					variant="tertiary"
					onClick={showQuestionModal}
					tooltip="Ask data team"
				/>
			);
		}

		return (
			<Button variant="default" onClick={showQuestionModal}>
				<Group spacing="4xs">
					<Icon name="messageCircleQuestion" />
					<Text size="xs" weight="semibold">
						Ask data team
					</Text>
				</Group>
			</Button>
		);
	}
);
